'use client';

/**
 * GlobalAnalyticsTracker
 *
 * 전역 Analytics 이벤트 트래킹:
 * 1. 스크롤 깊이 트래킹 (25%, 50%, 75%, 100%)
 * 2. 페이지 체류 시간 트래킹
 *
 * @note AnalyticsProvider와 분리된 이유:
 * - Provider는 context/설정만 담당 (Super Properties)
 * - 이벤트 트래킹 로직은 features 계층에서 처리
 */
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { Analytics } from './tracker';

// 스크롤 핸들러 throttle 유틸리티 (외부 라이브러리 불필요)
function throttle<T extends (...args: any[]) => void>(fn: T, delay: number): T {
  let lastCall = 0;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  return ((...args: any[]) => {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      fn(...args);
    } else if (!timeoutId) {
      timeoutId = setTimeout(
        () => {
          lastCall = Date.now();
          timeoutId = null;
          fn(...args);
        },
        delay - (now - lastCall),
      );
    }
  }) as T;
}

const SCROLL_THRESHOLDS = [25, 50, 75, 100];
const MIN_TIME_SECONDS = 3; // 최소 3초 이상 체류해야 트래킹
const MAX_TIME_SECONDS = 1800; // 최대 30분

// pathname → 페이지 이름 변환
function getPageName(pathname: string): string {
  // 정확한 매칭
  if (pathname === '/' || pathname === '/challenges') {
    return 'Home';
  }

  // prefix 매칭
  const prefixMap: Record<string, string> = {
    '/challenges/': 'Challenge Detail',
    '/submit/': 'Submit',
    '/leaderboard': 'Leaderboard',
    '/profile/': 'Profile',
    '/mypage': 'My Page',
    '/login': 'Login',
    '/welcome': 'Welcome',
    '/admin': 'Admin',
  };

  for (const [prefix, pageName] of Object.entries(prefixMap)) {
    if (pathname.startsWith(prefix)) {
      return pageName;
    }
  }

  // fallback: pathname의 첫 segment를 capitalize
  const segment = pathname.split('/')[1] || 'Home';
  return segment.charAt(0).toUpperCase() + segment.slice(1);
}

/**
 * 전역 Analytics 이벤트 트래커
 *
 * app/layout.tsx에서 AnalyticsProvider와 함께 사용:
 * ```tsx
 * <AnalyticsProvider>
 *   <GlobalAnalyticsTracker />
 *   {children}
 * </AnalyticsProvider>
 * ```
 */
export function GlobalAnalyticsTracker() {
  const pathname = usePathname();
  const reachedThresholdsRef = useRef<Set<number>>(new Set());
  const pageStartTimeRef = useRef<number>(Date.now());
  const previousPathnameRef = useRef<string | null>(null);

  // 🎯 전역 스크롤 깊이 트래킹
  const handleScroll = useCallback(() => {
    if (!pathname) {
      return;
    }

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = window.innerHeight;

    if (scrollHeight <= clientHeight) {
      return;
    }

    const scrollPercentage = Math.round((scrollTop / (scrollHeight - clientHeight)) * 100);
    const pageName = getPageName(pathname);

    SCROLL_THRESHOLDS.forEach(threshold => {
      if (scrollPercentage >= threshold && !reachedThresholdsRef.current.has(threshold)) {
        reachedThresholdsRef.current.add(threshold);
        Analytics.track('Scroll Page', {
          page_path: pathname,
          page_name: pageName,
          scroll_threshold: threshold,
          scroll_percentage: scrollPercentage,
        });
      }
    });
  }, [pathname]);

  // 🎯 throttled 스크롤 핸들러 (200ms 간격)
  const throttledHandleScroll = useMemo(() => throttle(handleScroll, 200), [handleScroll]);

  // 🎯 스크롤 이벤트 리스너 설정
  useEffect(() => {
    if (!pathname) {
      return;
    }

    // 페이지 변경 시 threshold 초기화
    reachedThresholdsRef.current = new Set();

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });
    handleScroll(); // 초기 체크 (throttle 없이 즉시 실행)

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [pathname, handleScroll, throttledHandleScroll]);

  // 🎯 페이지 체류 시간 트래킹
  useEffect(() => {
    if (!pathname) {
      return;
    }

    // 페이지 변경 시 이전 페이지의 체류 시간 트래킹
    if (previousPathnameRef.current && previousPathnameRef.current !== pathname) {
      const elapsedMs = Date.now() - pageStartTimeRef.current;
      const elapsedSeconds = Math.round(elapsedMs / 1000);

      if (elapsedSeconds >= MIN_TIME_SECONDS) {
        const timeSeconds = Math.min(elapsedSeconds, MAX_TIME_SECONDS);
        const pageName = getPageName(previousPathnameRef.current);

        Analytics.track('Time Spent', {
          page_path: previousPathnameRef.current,
          page_name: pageName,
          time_seconds: timeSeconds,
        });
      }
    }

    // 새 페이지 시작 시간 기록
    previousPathnameRef.current = pathname;
    pageStartTimeRef.current = Date.now();

    // 페이지 언로드 시 체류 시간 트래킹 (beforeunload)
    const handleBeforeUnload = () => {
      const elapsedMs = Date.now() - pageStartTimeRef.current;
      const elapsedSeconds = Math.round(elapsedMs / 1000);

      if (elapsedSeconds >= MIN_TIME_SECONDS) {
        const timeSeconds = Math.min(elapsedSeconds, MAX_TIME_SECONDS);
        const pageName = getPageName(pathname);

        // sendBeacon으로 페이지 종료 시에도 안정적으로 전송
        // Analytics.track은 beforeunload에서 신뢰할 수 없으므로 sendBeacon 직접 사용
        if (navigator.sendBeacon) {
          navigator.sendBeacon(
            'https://crm-backend-gbhojycnbq-uc.a.run.app/api/events/beacon',
            JSON.stringify({
              event: 'Time Spent',
              properties: {
                page_path: pathname,
                page_name: pageName,
                time_seconds: timeSeconds,
              },
            }),
          );
        }

        // 일반 track도 시도 (sendBeacon 미지원 브라우저 fallback)
        Analytics.track('Time Spent', {
          page_path: pathname,
          page_name: pageName,
          time_seconds: timeSeconds,
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [pathname]);

  return null;
}
